import { useState } from "react";
import Swal from "sweetalert2";
import ApiService from "../../api/apiService";
import { useSupport, myRole, runSessionAction } from "../../hooks/supportStore";
import { confirmAction, showSupportError, timeAgo, useNow, STATUS_LABELS } from "./support-helpers";

const presenceLabel = { online: "Available", away: "Away", offline: "Offline" };

async function pickTransferTarget(currentOwnerId) {
  const res = await ApiService.support.listAgents();
  if (!res.success) {
    showSupportError(res.message);
    return null;
  }
  const options = {};
  res.agents
    .filter((agent) => agent.id !== currentOwnerId)
    .forEach((agent) => {
      options[agent.id] = `${agent.display_name} — ${presenceLabel[agent.presence] || agent.presence}, ${agent.active_chats} active`;
    });
  if (!Object.keys(options).length) {
    showSupportError("There are no other customer support agents to transfer to.");
    return null;
  }
  const { value } = await Swal.fire({
    title: "Transfer chat",
    input: "select",
    inputOptions: options,
    inputPlaceholder: "Choose an agent",
    showCancelButton: true,
    confirmButtonText: "Transfer",
    confirmButtonColor: "#9d0001",
    inputValidator: (v) => (!v ? "Choose an agent" : undefined),
  });
  return value || null;
}

function Field({ label, children }) {
  if (!children) return null;
  return (
    <div className="cs-field">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function SupportCustomerCard({ sessionId, onClose }) {
  const support = useSupport();
  const now = useNow(30000);
  const session = support.sessions[sessionId];
  const [busy, setBusy] = useState(false);
  if (!session) return null;

  const myId = support.me?.id;
  const role = myRole(session, myId);
  const isOwnerOrSupervisor = role === "owner" || support.me?.cs_supervisor;
  const meta = session.customer_meta || {};

  const run = async (action, ...args) => {
    setBusy(true);
    const res = await runSessionAction(action, sessionId, ...args);
    setBusy(false);
    if (!res.success) showSupportError(res.message);
    return res;
  };

  const transfer = async () => {
    const target = await pickTransferTarget(session.owner_user_id);
    if (target) run("transfer", target);
  };

  return (
    <aside className="cs-card" aria-label="Customer details">
      <div className="cs-card__head">
        <h3>{session.customer_name}</h3>
        {onClose && (
          <button type="button" className="cs-icon-btn" onClick={onClose} aria-label="Close details">
            <i className="fa fa-times" aria-hidden />
          </button>
        )}
      </div>
      <span className={`cs-tag ${session.customer_verified ? "cs-tag--verified" : ""}`}>
        {session.customer_verified ? "Verified BuildHub customer" : "Guest (details not verified)"}
      </span>

      <dl className="cs-fields">
        <Field label="Email">
          {session.customer_email && <a href={`mailto:${session.customer_email}`}>{session.customer_email}</a>}
        </Field>
        <Field label="Mobile">
          {session.customer_phone && <a href={`tel:${session.customer_phone}`}>{session.customer_phone}</a>}
        </Field>
        <Field label="Business">{session.customer_company}</Field>
        <Field label="Customer ID">{session.customer_external_id}</Field>
        <Field label="Account">{[meta.accountType, meta.buyerType].filter(Boolean).join(" · ") || null}</Field>
        <Field label="OTP verified">{meta.otpVerified ? "Yes" : null}</Field>
        <Field label="Ticket">{session.ticket_id}</Field>
        <Field label="Reason">{session.escalate_reason?.replace(/_/g, " ")}</Field>
        <Field label="Status">{STATUS_LABELS[session.status]}</Field>
        <Field label="Queued">
          {session.queued_at &&
            (timeAgo(session.queued_at, now) === "just now" ? "just now" : `${timeAgo(session.queued_at, now)} ago`)}
        </Field>
        <Field label="Rating">
          {session.csat_rating ? `${"★".repeat(session.csat_rating)}${session.csat_comment ? ` — ${session.csat_comment}` : ""}` : null}
        </Field>
      </dl>

      {session.agents?.length > 0 && (
        <>
          <h4 className="cs-card__subhead">Agents in this chat</h4>
          <ul className="cs-card__agents">
            {session.agents.map((a) => (
              <li key={a.user_id}>
                {a.display_name}
                <span>{a.role === "owner" ? "Owner" : "Assisting"}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="cs-card__actions">
        {session.status === "waiting" && (
          <button type="button" className="cs-btn cs-btn--primary" disabled={busy} onClick={() => run("claim")}>
            Accept chat
          </button>
        )}
        {session.status === "active" && !role && (
          <button type="button" className="cs-btn cs-btn--primary" disabled={busy} onClick={() => run("join")}>
            Join to assist
          </button>
        )}
        {session.status === "active" && isOwnerOrSupervisor && (
          <>
            <button
              type="button"
              className="cs-btn cs-btn--primary"
              disabled={busy}
              onClick={async () => {
                if (await confirmAction("Resolve this chat?", "The customer will be asked to rate the chat.", "Resolve"))
                  run("resolve");
              }}
            >
              Resolve
            </button>
            <button type="button" className="cs-btn" disabled={busy} onClick={transfer}>
              Transfer…
            </button>
            <button
              type="button"
              className="cs-btn"
              disabled={busy}
              onClick={async () => {
                if (await confirmAction("Return to queue?", "Another agent will pick up this customer.", "Return to queue"))
                  run("requeue");
              }}
            >
              Return to queue
            </button>
          </>
        )}
        {session.status === "active" && role === "assistant" && (
          <button type="button" className="cs-btn" disabled={busy} onClick={() => run("leave")}>
            Leave chat
          </button>
        )}
      </div>
    </aside>
  );
}

export default SupportCustomerCard;
